import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ElementRef, inject, Injectable, Injector } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { CustomContextMenuComponent, CustomContextMenuItem } from './custom-context-menu.component'; // Adjust path

export interface ContextMenuSelection {
  actionId: string;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class CustomContextMenuService {
  private overlay = inject(Overlay);
  private injector = inject(Injector); // For creating PortalInjector if passing data via DI

  private currentOverlayRef: OverlayRef | null = null;

  constructor() {}

  /**
   * Opens the custom context menu.
   * @param triggerElement The element that triggered the menu (for positioning).
   * @param menuItems The array of items to display in the menu.
   * @param associatedData Optional data to associate with the menu instance, can be passed to items.
   * @returns An Observable that emits the selected menu item's action and data, then completes.
   */
  public open(
    triggerElement: ElementRef | HTMLElement,
    menuItems: CustomContextMenuItem[],
    associatedData?: any // Optional data that might be useful for all items
  ): Observable<ContextMenuSelection> {
    this.close(); // Close any existing menu

    const positionStrategy = this.getPositionStrategy(triggerElement);

    this.currentOverlayRef = this.overlay.create({
      positionStrategy: positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    // Create an injector that can provide data to the component if needed (optional)
    // const injector = this.createInjector({ menuItems, associatedData });

    const menuPortal = new ComponentPortal(CustomContextMenuComponent, null /* viewContainerRef */, this.injector);
    const componentRef = this.currentOverlayRef.attach(menuPortal);
    componentRef.instance.menuItems = menuItems;
    // componentRef.instance.position = {} // Positioning is now handled by CDK Overlay

    const selectionSubject = new Subject<ContextMenuSelection>();

    // Subscribe to item selection from the menu component
    const itemSelectedSub = componentRef.instance.itemSelected.pipe(take(1)).subscribe((selection) => {
      selectionSubject.next({ actionId: selection.actionId, data: selection.data || associatedData });
      selectionSubject.complete();
      this.close();
    });

    // Subscribe to backdrop click or escape key to close
    const backdropClickSub = this.currentOverlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        selectionSubject.complete(); // Complete without emitting a value
        this.close();
      });

    // Could also listen for ESCAPE key on the overlayRef if needed

    // When the overlay is detached (closed), make sure our subject completes
    const detachmentsSub = this.currentOverlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        selectionSubject.complete();
        this.menuClosedCleanup(itemSelectedSub, backdropClickSub, detachmentsSub);
      });

    componentRef.instance.menuClosed.pipe(take(1)).subscribe(() => {
      selectionSubject.complete();
      this.close(); // Ensure service's close is called
    });

    return selectionSubject.asObservable();
  }

  /**
   * Closes the currently open context menu.
   */
  public close(): void {
    if (this.currentOverlayRef && this.currentOverlayRef.hasAttached()) {
      this.currentOverlayRef.detach(); // This will trigger the detachments observable
    }
    // Cleanup is handled by detachments subscription
  }

  private menuClosedCleanup(...subs: Subscription[]): void {
    subs.forEach((sub) => sub.unsubscribe());
    if (this.currentOverlayRef) {
      this.currentOverlayRef.dispose();
      this.currentOverlayRef = null;
    }
  }

  private getPositionStrategy(trigger: ElementRef | HTMLElement): FlexibleConnectedPositionStrategy {
    const element = trigger instanceof ElementRef ? trigger.nativeElement : trigger;
    return this.overlay
      .position()
      .flexibleConnectedTo(element)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: -2 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: -2 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
      ])
      .withPush(true);
  }

  // Optional: If you needed to pass large amounts of data or services via DI to the portal
  // private createInjector(dataToPass: any): PortalInjector {
  //   const injectionTokens = new WeakMap();
  //   injectionTokens.set(YOUR_CUSTOM_TOKEN, dataToPass); // Define YOUR_CUSTOM_TOKEN
  //   return new PortalInjector(this.injector, injectionTokens);
  // }
}
